import React, { useContext } from 'react'
import classNames from 'classnames'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Avatar } from 'browser/components/atomic-elements/atoms/avatar/avatar'
import { HelpBlock } from 'browser/components/atomic-elements/atoms/help-block/help-block'
import { Label } from 'browser/components/atomic-elements/atoms/label/label'
import { InternalViewContext } from 'browser/contexts/internal-view/internal-view-context'
import { Owner } from 'shared-libs/generated/server-types/entity'
import { renderAuthorHeader, renderEventTimestamp } from '../util'
import { Position, Tooltip } from '@blueprintjs/core'

interface IExecuteApiDefinitionResultItemProps extends IBaseProps {
  event: any
  owner: Owner
  index: number
  isFirst: boolean
  isLast: boolean
  workflowEntity: any
}

export const ExecuteApiDefinitionResultItem: React.FC<IExecuteApiDefinitionResultItemProps> = (props) => {
  const { className, event, owner, isFirst } = props
  const { createdBy, creationDate, processedDate } = event
  const { showInternals } = useContext(InternalViewContext)

  const meta = event._apiExecutionMeta
  if (!meta) {
    return null
  }

  const { referenceKey, success, statusCode, errorMsg, statusMessage, durationMs, requestMethod, requestUrl } = meta
  const failureReason = errorMsg || statusMessage

  const failureText = ['Failed', statusCode && `(${statusCode})`, failureReason && `— ${failureReason}`]
    .filter(Boolean).join(' ')
  const statusText = success ? `${statusCode} — Success` : failureText

  const tooltip = (
    <div>
      <div className="mb1"><b>API call: {referenceKey}</b></div>
      {requestMethod && requestUrl && (
        <div className="mb1">{requestMethod} {requestUrl}</div>
      )}
      <div>{statusText}</div>
      {durationMs != null && <div className="mt1 black-50">{durationMs}ms</div>}
    </div>
  )

  return (
    <div className={classNames('c-timelineEvent', className)} key={event.id}>
      <div
        className={classNames('c-timelineEvent-line', className, {
          'c-timelineEvent-line--topCutOff': isFirst,
        })}
      />
      <div className="c-timelineEvent-dot" />
      <div className="c-timelineEvent-contentContainer">
        {createdBy && (
          <div className="c-timelineEvent-header">
            <div className="c-timelineEvent-author">
              <Avatar
                className="c-timelineEvent-avatar"
                name={createdBy.displayName || 'System User'}
                size="xs"
              />
              <Label className="c-timelineEvent-authorName">
                {renderAuthorHeader(createdBy, owner?.firm)}
              </Label>
            </div>
            <HelpBlock className="c-timelineEvent-timestamp">
              {renderEventTimestamp(creationDate, processedDate, showInternals)}
            </HelpBlock>
          </div>
        )}
        <div className="c-timelineEvent-body">
          <Tooltip content={tooltip} position={Position.TOP} fill={false}>
            <div>
              API call <b>{referenceKey}</b>{' '}
              {success
                ? <span>succeeded ({statusCode})</span>
                : <span>failed{statusCode ? ` (${statusCode})` : ''}</span>
              }
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
